import { type ReactElement, useState } from 'react';
import { useTranslation } from 'react-i18next';
import ReactMarkdown from 'react-markdown';
import Loader from 'components/Loader/Loader';
import remarkGfm from 'remark-gfm';
import {
  useGetCriticalChangesQuery,
  useGetReleaseVersionsQuery,
} from 'store/slices/releaseHistory/apis/releaseHistoryApi';

import {
  criticalChangesEmptyText,
  EXAMINATION_FROM,
  EXAMINATION_TO,
  formatReleaseDate,
} from './releaseHistoryFormat';

import styles from './sass/ReleaseHistory.module.scss';

type ReleaseHistoryTab = 'critical' | 'releases';

const MARKDOWN_PLUGINS = [remarkGfm];

function ReleaseHistory(): ReactElement {
  const { t } = useTranslation();
  const [tab, setTab] = useState<ReleaseHistoryTab>('critical');
  const [from, setFrom] = useState(EXAMINATION_FROM);
  const [to, setTo] = useState(EXAMINATION_TO);
  const [openVersion, setOpenVersion] = useState<string | null>(null);

  const versionsQuery = useGetReleaseVersionsQuery();
  const criticalQuery = useGetCriticalChangesQuery({ from, to });
  const versions = versionsQuery.data?.data ?? [];
  const criticalRows = criticalQuery.data?.data ?? [];
  const openRelease = versions.find((item) => item.version === openVersion) || null;

  return (
    <div className={styles.root}>
      <h1 className={styles.title}>{t('releaseHistory.title')}</h1>
      <div className={styles.tabs} role="tablist" aria-label={t('releaseHistory.title')}>
        <button
          type="button"
          role="tab"
          aria-selected={tab === 'critical'}
          className={tab === 'critical' ? `${styles.tab} ${styles.tabActive}` : styles.tab}
          onClick={() => setTab('critical')}
        >
          {t('releaseHistory.tabCritical')}
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={tab === 'releases'}
          className={tab === 'releases' ? `${styles.tab} ${styles.tabActive}` : styles.tab}
          onClick={() => setTab('releases')}
        >
          {t('releaseHistory.tabReleases')}
        </button>
      </div>

      {tab === 'critical' ? (
        <div role="tabpanel">
          <div className={styles.range}>
            <label className={styles.field} htmlFor="release-history-from">
              {t('releaseHistory.from')}
              <input
                id="release-history-from"
                type="date"
                value={from}
                onChange={(event) => setFrom(event.target.value)}
              />
            </label>
            <label className={styles.field} htmlFor="release-history-to">
              {t('releaseHistory.to')}
              <input
                id="release-history-to"
                type="date"
                value={to}
                onChange={(event) => setTo(event.target.value)}
              />
            </label>
          </div>
          {criticalQuery.isError ? (
            <p className={styles.empty}>{t('releaseHistory.loadError')}</p>
          ) : null}
          {criticalQuery.isLoading ? <Loader isPageLoading isFullLoader={false} /> : null}
          {!criticalQuery.isLoading && !criticalQuery.isError && criticalRows.length === 0 ? (
            <p className={styles.empty}>{criticalChangesEmptyText(from, to)}</p>
          ) : null}
          {!criticalQuery.isLoading && !criticalQuery.isError && criticalRows.length > 0 ? (
            <div className={styles.tableWrap}>
              <table className={styles.table}>
                <thead>
                  <tr>
                    <th>{t('releaseHistory.version')}</th>
                    <th>{t('releaseHistory.publishedDate')}</th>
                    <th>{t('releaseHistory.changeTitle')}</th>
                    <th>{t('releaseHistory.whatChanged')}</th>
                    <th>{t('releaseHistory.customerImpact')}</th>
                    <th>{t('releaseHistory.actionRequired')}</th>
                    <th>{t('releaseHistory.effectiveDate')}</th>
                  </tr>
                </thead>
                <tbody>
                  {criticalRows.map((row) => {
                    const published = formatReleaseDate(row.publishedDate);
                    return (
                      <tr key={row.id}>
                        <td>{row.version}</td>
                        <td>{published}</td>
                        <td>{row.title}</td>
                        <td>{row.whatChanged}</td>
                        <td>{row.customerImpact}</td>
                        <td>{row.actionRequired}</td>
                        <td>{published}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          ) : null}
        </div>
      ) : (
        <div role="tabpanel">
          {versionsQuery.isError ? (
            <p className={styles.empty}>{t('releaseHistory.loadError')}</p>
          ) : null}
          {versionsQuery.isLoading ? <Loader isPageLoading isFullLoader={false} /> : null}
          {!versionsQuery.isLoading && !versionsQuery.isError && versions.length === 0 ? (
            <p className={styles.empty}>{t('releaseHistory.releasesEmpty')}</p>
          ) : null}
          {!versionsQuery.isLoading && !versionsQuery.isError && versions.length > 0 ? (
            <div className={styles.tableWrap}>
              <table className={styles.table}>
                <thead>
                  <tr>
                    <th className={styles.shrink}>{t('releaseHistory.version')}</th>
                    <th className={styles.shrink}>{t('releaseHistory.publishedDate')}</th>
                    <th>{t('releaseHistory.summary')}</th>
                    <th className={styles.shrink}>{t('releaseHistory.details')}</th>
                  </tr>
                </thead>
                <tbody>
                  {versions.map((row) => (
                    <tr
                      key={row.version}
                      className={openVersion === row.version ? `${styles.row} ${styles.rowSelected}` : styles.row}
                      onClick={() => setOpenVersion(row.version)}
                    >
                      <td className={styles.shrink}>{row.version}</td>
                      <td className={styles.shrink}>{formatReleaseDate(row.publishedDate)}</td>
                      <td>{row.summary}</td>
                      <td className={styles.view}>{t('releaseHistory.viewDetails')}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : null}
          {openRelease ? (
            <section className={styles.details} aria-label={openRelease.version}>
              <div className={styles.detailsHeader}>
                <h2 className={styles.detailsTitle}>{openRelease.version}</h2>
                <button type="button" className={styles.close} onClick={() => setOpenVersion(null)}>
                  {t('releaseHistory.closeDetails')}
                </button>
              </div>
              <div className={styles.markdown}>
                <ReactMarkdown remarkPlugins={MARKDOWN_PLUGINS}>
                  {openRelease.detailsMarkdown}
                </ReactMarkdown>
              </div>
            </section>
          ) : null}
        </div>
      )}
    </div>
  );
}

export default ReleaseHistory;
